import React, { useLayoutEffect, useRef } from 'react';
import { useIsMounted } from '@hooks/useIsMounted';
import { useScrollController } from '@context/ScrollControllerProvider/useScrollController';
import ScrollMagic from 'scrollmagic';
import ReactHtmlParser from 'react-html-parser';
import { JoykitColor } from '@components/themes';
import { CTALink } from '@components/CTALink';
import { JoykitIcon, JoykitIconProps, JoykitIconVariant } from '@components/JoykitIcon';
import { Box, BoxProps } from '@components/Box';
import { Columns } from '@components/Columns';
import {
  BottomAlignedColumns,
  StyledText,
  TitleTextWrapper,
  ButtonWrapper,
  RSVPTrackingColumns,
  WeddingFirstColumns,
  MainLottieWrapper,
  ColumnsContainer,
} from './RSVPTrackingSection.styles';
import { onlineRsvpPageData } from '@data-ts/pages/online-rsvp';
import { guestListData } from '@data-ts/pages/guest-list';
import { SectionData } from '@data-ts/types/SectionData';
import { cloudAssetUrl } from '@utils/cloudAssetUrl.util';
import { useLottieAnimation } from '@hooks/useLottieAnimation';
import { LazyLoadWrapper } from '@components/LazyLoadWrapper';
import { useMediaQuery } from 'react-responsive';

const rsvpTrackingData = onlineRsvpPageData.rsvpTracking;

interface RSVPTrackingPageContextTypes
  extends Readonly<{
    guestList: {
      accentColor: JoykitColor;
      textColor: JoykitColor;
      lottieJsonUrl: string;
      lottieJsonUrlA: string;
      lottieJsonUrlB: string;
      lottieJsonUrlC: string;
      lottieJsonUrlD: string;
      lottieJsonUrlE: string;
      data: SectionData;
    };
    weddingInvitations: {
      accentColor: JoykitColor;
      textColor: JoykitColor;
      lottieJsonUrl: string;
      lottieJsonUrlA: string;
      lottieJsonUrlB: string;
      lottieJsonUrlC: string;
      lottieJsonUrlD: string;
      lottieJsonUrlE: string;
      data: SectionData;
    };
  }> {}

const RSVPTrackingPageContext: RSVPTrackingPageContextTypes = {
  guestList: {
    accentColor: 'purple4',
    textColor: 'purple5',
    lottieJsonUrl: cloudAssetUrl('guest-list/response-tracking/bodymovin/data.json'),
    lottieJsonUrlA: cloudAssetUrl('wedding-invitations/response-tracking/bodymovin/c/data.json'),
    lottieJsonUrlB: cloudAssetUrl('wedding-invitations/response-tracking/bodymovin/d/data.json'),
    lottieJsonUrlC: cloudAssetUrl('wedding-invitations/response-tracking/bodymovin/e/data.json'),
    lottieJsonUrlD: cloudAssetUrl('wedding-invitations/response-tracking/bodymovin/a/data.json'),
    lottieJsonUrlE: cloudAssetUrl('wedding-invitations/response-tracking/bodymovin/b/data.json'),
    data: guestListData.rsvpTracking,
  },
  weddingInvitations: {
    accentColor: 'blue5',
    textColor: 'blue6',
    lottieJsonUrl: cloudAssetUrl('wedding-invitations/response-tracking/bodymovin/data.json'),
    lottieJsonUrlA: cloudAssetUrl('wedding-invitations/response-tracking/bodymovin/a/data.json'),
    lottieJsonUrlB: cloudAssetUrl('wedding-invitations/response-tracking/bodymovin/b/data.json'),
    lottieJsonUrlC: cloudAssetUrl('wedding-invitations/response-tracking/bodymovin/c/data.json'),
    lottieJsonUrlD: cloudAssetUrl('wedding-invitations/response-tracking/bodymovin/d/data.json'),
    lottieJsonUrlE: cloudAssetUrl('wedding-invitations/response-tracking/bodymovin/e/data.json'),
    data: onlineRsvpPageData.rsvpTracking,
  },
};
interface RSVPTrackingCount
  extends Readonly<{
    number: string;
    icon: JoykitIconVariant;
    text: string;
  }> {}

const RSVPTrackingIcon: React.FC<Omit<JoykitIconProps, 'color'>> = ({ ...iconProps }) => {
  return <JoykitIcon color="purple4" {...iconProps} />;
};

interface RSVPTrackingSectionProps
  extends Readonly<{
    page: 'guestList' | 'weddingInvitations';
    backgroundColor?: BoxProps['backgroundColor'];
  }> {}

interface RSVPTrackingSectionDesktopProps
  extends Readonly<{
    page: 'guestList' | 'weddingInvitations';
    backgroundColor?: BoxProps['backgroundColor'];
  }> {}

interface RSVPTrackingSectionMobileProps
  extends Readonly<{
    page: 'guestList' | 'weddingInvitations';
    backgroundColor?: BoxProps['backgroundColor'];
  }> {}

type AnimationConfigWithForcePath = {
  renderer: 'svg';
  loop: boolean;
  autoplay: boolean;
  rendererSettings: {
    progressiveLoad: boolean;
    preserveAspectRatio: 'xMidYMid slice';
  };
};

const defaultLottieConfig: AnimationConfigWithForcePath = {
  renderer: 'svg', // Required
  loop: false, // Optional
  autoplay: false, // Optional
  rendererSettings: {
    progressiveLoad: false,
    preserveAspectRatio: 'xMidYMid slice',
  },
};

const RSVPTrackingSectionDesktop: React.FC<RSVPTrackingSectionDesktopProps> = ({ page, backgroundColor = 'yellow1' }) => {
  const pageContext = page === 'guestList' ? RSVPTrackingPageContext.guestList : RSVPTrackingPageContext.weddingInvitations;
  const lottieRefA = useRef<HTMLDivElement>(null);
  const lottieRefB = useRef<HTMLDivElement>(null);
  const lottieRefC = useRef<HTMLDivElement>(null);
  const lottieRefE = useRef<HTMLDivElement>(null);
  const lottieRefD = useRef<HTMLDivElement>(null);
  const scrollController = useScrollController();
  const isMounted = useIsMounted();
  const { animationRef: loadAnimationD, loadAnimation: loadFirst } = useLottieAnimation(
    {
      ...defaultLottieConfig,
      path: pageContext.lottieJsonUrlD,
      name: 'guestlist features1',
    },
    { containerRef: lottieRefD, speed: 2.5 }
  );
  const { animationRef: loadAnimationE, loadAnimation: loadSecond } = useLottieAnimation(
    {
      ...defaultLottieConfig,
      path: pageContext.lottieJsonUrlE,
      name: 'guestlist features2',
    },
    { containerRef: lottieRefE, speed: 2.5 }
  );
  const { animationRef: loadAnimationA, loadAnimation: loadThird } = useLottieAnimation(
    {
      ...defaultLottieConfig,
      path: pageContext.lottieJsonUrlA,
      name: 'guestlist features3',
    },
    { containerRef: lottieRefA }
  );
  const { animationRef: loadAnimationB, loadAnimation: loadFour } = useLottieAnimation(
    {
      ...defaultLottieConfig,
      path: pageContext.lottieJsonUrlB,
      name: 'guestlist features',
    },
    { containerRef: lottieRefB }
  );
  const { animationRef: loadAnimationC, loadAnimation: loadFive } = useLottieAnimation(
    {
      ...defaultLottieConfig,
      path: pageContext.lottieJsonUrlC,
      name: 'guestlist features',
    },
    { containerRef: lottieRefC }
  );

  const loadLotties = () => {
    loadFirst();
    loadSecond();
    loadThird();
    loadFour();
    loadFive();
  };

  useLayoutEffect(() => {
    let scene: InstanceType<typeof ScrollMagic.Scene> | undefined;
    if (scrollController && isMounted) {
      const startScrollAnimation = () => {
        scene = new ScrollMagic.Scene({
          triggerElement: '#RSVP_Tracking',
          triggerHook: 'onEnter',
          duration: page === 'guestList' ? 0 : '230%',
          offset: page === 'guestList' ? 0 : 100,
        })
          .addTo(scrollController)
          .on('enter', () => {
            loadAnimationD.current?.goToAndPlay(0);
            loadAnimationE.current?.goToAndPlay(0);
            loadAnimationA.current?.goToAndPlay(0);
            loadAnimationB.current?.goToAndPlay(0);
            loadAnimationC.current?.goToAndPlay(0);
          });
      };
      startScrollAnimation();
    }
    const clearScene = () => {
      if (scene) {
        scene.destroy(true);
      }
    };
    return clearScene;
  }, [scrollController, isMounted, loadAnimationA, loadAnimationB, loadAnimationC, loadAnimationD, loadAnimationE, page]);

  const guestFeaturePage = page === 'guestList';

  return (
    <Box as={'section'} paddingTop={8} paddingBottom={[9]} backgroundColor={backgroundColor} id="RSVP_Tracking">
      <LazyLoadWrapper beforeLoad={loadLotties}>
        <div />
      </LazyLoadWrapper>
      {!guestFeaturePage && (
        <BottomAlignedColumns>
          <Columns.Column offset="one" width="six">
            <TitleTextWrapper
              eyebrow={pageContext.data.eyebrow}
              eyebrowColor={pageContext.accentColor}
              eyebrowVariant={'sectionEyeBrow24'}
              eyebrowTagName="h2"
              title={ReactHtmlParser(pageContext.data.title)}
              titleMaxWidth="286px"
              titleVariant={'hed72Hero'}
              titleColor={pageContext.textColor}
              titleTagName="h2"
              marginBottom={[8, 7, 0]}
            />
          </Columns.Column>
          <Columns.Column width="four">
            <Box>
              <StyledText className="text-pairing-body" variant={'dek20'} tagName="p" color={pageContext.textColor} marginBottom={0}>
                {rsvpTrackingData.content}
              </StyledText>
              <ButtonWrapper>
                <CTALink id="LearnMoreAboutRSVPTracking" aria-label="Wedding Invitations" variant="primary" href="/wedding-invitations/">
                  {rsvpTrackingData.learnMore}
                </CTALink>
              </ButtonWrapper>
            </Box>
          </Columns.Column>
        </BottomAlignedColumns>
      )}
      <MainLottieWrapper>
        <RSVPTrackingColumns>
          <ColumnsContainer offset="one" width="five">
            <Box ref={lottieRefD} marginTop={guestFeaturePage ? 1 : 0} />
          </ColumnsContainer>
          <ColumnsContainer width="five">
            <Box ref={lottieRefE} marginLeft={7} />
            {guestFeaturePage && <Box ref={lottieRefA} marginLeft={7} />}
          </ColumnsContainer>
        </RSVPTrackingColumns>
        <RSVPTrackingColumns>
          <ColumnsContainer offset="one" width="five">
            <Box ref={lottieRefB} marginTop={!guestFeaturePage ? 3 : 1} />
            {page !== 'guestList' && <Box ref={lottieRefC} />}
          </ColumnsContainer>
          <ColumnsContainer width="five">
            <Box ref={guestFeaturePage ? lottieRefC : lottieRefA} marginLeft={7} />
          </ColumnsContainer>
        </RSVPTrackingColumns>
      </MainLottieWrapper>
    </Box>
  );
};

const RSVPTrackingSectionMobile: React.FC<RSVPTrackingSectionMobileProps> = ({ page, backgroundColor = 'yellow1' }) => {
  const pageContext = page === 'guestList' ? RSVPTrackingPageContext.guestList : RSVPTrackingPageContext.weddingInvitations;
  const lottieRefA = useRef<HTMLDivElement>(null);
  const lottieRefB = useRef<HTMLDivElement>(null);
  const lottieRefC = useRef<HTMLDivElement>(null);
  const lottieRefE = useRef<HTMLDivElement>(null);
  const lottieRefD = useRef<HTMLDivElement>(null);
  const scrollController = useScrollController();
  const isMounted = useIsMounted();
  const FirstTrackingColumn = page === 'guestList' ? WeddingFirstColumns : RSVPTrackingColumns;
  const { animationRef: loadAnimationD, loadAnimation: loadFirst } = useLottieAnimation(
    {
      path: pageContext.lottieJsonUrlD,
      renderer: 'svg', // Required
      loop: false, // Optional
      autoplay: false, // Optional
      name: 'guestlist features1',
      rendererSettings: {
        progressiveLoad: false,
        preserveAspectRatio: 'xMidYMid slice',
      },
    },
    { containerRef: lottieRefD, speed: 2.5 }
  );
  const { animationRef: loadAnimationE, loadAnimation: loadSecond } = useLottieAnimation(
    {
      path: pageContext.lottieJsonUrlE,
      renderer: 'svg', // Required
      loop: false, // Optional
      autoplay: false, // Optional
      name: 'guestlist features2',
      rendererSettings: {
        progressiveLoad: false,
        preserveAspectRatio: 'xMidYMid slice',
      },
    },
    { containerRef: lottieRefE, speed: 2.5 }
  );
  const { animationRef: loadAnimationA, loadAnimation: loadThird } = useLottieAnimation(
    {
      path: pageContext.lottieJsonUrlA,
      renderer: 'svg', // Required
      loop: false, // Optional
      autoplay: false, // Optional
      name: 'guestlist features3',
      rendererSettings: {
        progressiveLoad: false,
        preserveAspectRatio: 'xMidYMid slice',
      },
    },
    { containerRef: lottieRefA }
  );
  const { animationRef: loadAnimationB, loadAnimation: loadFour } = useLottieAnimation(
    {
      path: pageContext.lottieJsonUrlB,
      renderer: 'svg', // Required
      loop: false, // Optional
      autoplay: false, // Optional
      name: 'guestlist features',
      rendererSettings: {
        progressiveLoad: false,
        preserveAspectRatio: 'xMidYMid slice',
      },
    },
    { containerRef: lottieRefB }
  );
  const { animationRef: loadAnimationC, loadAnimation: loadFive } = useLottieAnimation(
    {
      path: pageContext.lottieJsonUrlC,
      renderer: 'svg', // Required
      loop: false, // Optional
      autoplay: false, // Optional
      name: 'guestlist features',
      rendererSettings: {
        progressiveLoad: false,
        preserveAspectRatio: 'xMidYMid slice',
      },
    },
    { containerRef: lottieRefC }
  );

  const loadLotties = () => {
    loadFirst();
    loadSecond();
    loadThird();
    loadFour();
    loadFive();
  };

  useLayoutEffect(() => {
    let scene: InstanceType<typeof ScrollMagic.Scene> | undefined;
    if (scrollController && isMounted) {
      const startScrollAnimation = () => {
        scene = new ScrollMagic.Scene({
          triggerElement: '#RSVP_Tracking',
          triggerHook: 'onEnter',
          duration: page === 'guestList' ? 0 : '250%',
          offset: page === 'guestList' ? 0 : 300,
        })
          .addTo(scrollController)
          .on('enter', () => {
            loadAnimationD.current?.goToAndPlay(0);
            loadAnimationE.current?.goToAndPlay(0);
            loadAnimationA.current?.goToAndPlay(0);
            loadAnimationB.current?.goToAndPlay(0);
            loadAnimationC.current?.goToAndPlay(0);
          });
      };
      startScrollAnimation();
    }
    const clearScene = () => {
      if (scene) {
        scene.destroy(true);
      }
    };
    return clearScene;
  }, [scrollController, isMounted, loadAnimationA, loadAnimationB, loadAnimationC, loadAnimationD, loadAnimationE, page]);

  return (
    <Box as={'section'} paddingTop={page === 'guestList' ? 0 : 13} paddingBottom={[9]} backgroundColor={backgroundColor} id="RSVP_Tracking">
      <LazyLoadWrapper beforeLoad={loadLotties}>
        <div />
      </LazyLoadWrapper>
      {page === 'weddingInvitations' && (
        <BottomAlignedColumns>
          <Columns.Column width="seven">
            <TitleTextWrapper
              eyebrow={pageContext.data.eyebrow}
              eyebrowColor={pageContext.accentColor}
              eyebrowVariant={'sectionEyeBrow24'}
              eyebrowTagName="h2"
              title={ReactHtmlParser(pageContext.data.title)}
              titleMaxWidth="286px"
              titleVariant={'hed72Hero'}
              titleColor={pageContext.textColor}
              marginBottom={[8, 7, 0]}
            />
          </Columns.Column>
          <Columns.Column offset="one" width="four">
            <Box>
              <StyledText className="text-pairing-body" variant={'dek24'} tagName="p" color={pageContext.textColor} marginBottom={0}>
                {rsvpTrackingData.content}
              </StyledText>
              <ButtonWrapper>
                <CTALink id="LearnMoreAboutRSVPTrackingMobile" variant="primary" href="/wedding-invitations/">
                  {rsvpTrackingData.learnMore}
                </CTALink>
              </ButtonWrapper>
            </Box>
          </Columns.Column>
        </BottomAlignedColumns>
      )}
      <FirstTrackingColumn>
        <Columns.Column width="twelve">
          <Box ref={lottieRefD} />
        </Columns.Column>
      </FirstTrackingColumn>
      <RSVPTrackingColumns>
        <Columns.Column width="twelve">
          <Box ref={lottieRefE} />
        </Columns.Column>
      </RSVPTrackingColumns>
      <RSVPTrackingColumns>
        <Columns.Column width="twelve">
          <Box ref={lottieRefA} />
        </Columns.Column>
      </RSVPTrackingColumns>
      <RSVPTrackingColumns>
        <Columns.Column width="twelve">
          <Box ref={lottieRefB} />
        </Columns.Column>
      </RSVPTrackingColumns>
      <RSVPTrackingColumns>
        <Columns.Column width="twelve">
          <Box ref={lottieRefC} />
        </Columns.Column>
      </RSVPTrackingColumns>
    </Box>
  );
};

export const RSVPTrackingSection: React.FC<RSVPTrackingSectionProps> = ({ page, backgroundColor = 'yellow1' }) => {
  const isMobileDevice = useMediaQuery({
    query: '(max-width: 800px)',
  });

  // Conditionally render mobile version and desktop version.
  return isMobileDevice ? (
    <RSVPTrackingSectionMobile page={page} backgroundColor={backgroundColor} />
  ) : (
    <RSVPTrackingSectionDesktop page={page} backgroundColor={backgroundColor} />
  );
};

export default RSVPTrackingSection;
